/**
 * Filter modals and column sidebar panel for DocTypeListPageShell.
 * Handles filter merge logic (general vs advanced filters).
 *
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import { FilterModal } from "@/components/filter-builder/FilterModal";
import { AdvancedFilterModal } from "@/components/filter-builder/AdvancedFilterModal";
import { ColumnSidebarPanel } from "./ColumnSidebarPanel";
import type { FilterDescriptor } from "@/lib/erp";
import type { useMetaDrivenListSettings } from "@/lib/hooks/useMetaDrivenListSettings";

export type ListFilterModalsProps = {
  // Filter modal
  isFilterModalOpen: boolean;
  onFilterModalOpenChange: (open: boolean) => void;
  isAdvancedFilterModalOpen: boolean;
  onAdvancedFilterModalOpenChange: (open: boolean) => void;
  doctype: string;
  filters: FilterDescriptor[];
  onFiltersChange: (filters: FilterDescriptor[]) => void;
  translations: Record<string, string>;

  // Column sidebar
  isColumnSidebarOpen: boolean;
  onColumnSidebarClose: () => void;
  listSettings: ReturnType<typeof useMetaDrivenListSettings> | null;
  systemColumnToggles?: import("./ListSettingsModal").SystemColumnToggle[];
  onSystemColumnToggle?: (id: string, visible: boolean) => void;
};

export function ListFilterModals({
  isFilterModalOpen,
  onFilterModalOpenChange,
  isAdvancedFilterModalOpen,
  onAdvancedFilterModalOpenChange,
  doctype,
  filters,
  onFiltersChange,
  translations,
  isColumnSidebarOpen,
  onColumnSidebarClose,
  listSettings,
  systemColumnToggles,
  onSystemColumnToggle,
}: ListFilterModalsProps) {
  return (
    <>
      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={onFilterModalOpenChange}
        doctype={doctype}
        filters={filters}
        onChange={(newGeneralFilters) => {
          // Merge with existing advanced filters
          const advancedFilters = filters.filter(
            (f) =>
              f._metadata?.section &&
              (f._metadata.section === "assigned_to" ||
                f._metadata.section === "created_by" ||
                f._metadata.section === "tags")
          );
          onFiltersChange([...newGeneralFilters, ...advancedFilters]);
        }}
        translations={translations}
      />
      <AdvancedFilterModal
        open={isAdvancedFilterModalOpen}
        onOpenChange={onAdvancedFilterModalOpenChange}
        filters={filters}
        onChange={(newAdvancedFilters) => {
          // Merge with existing general filters
          const generalFilters = filters.filter(
            (f) => !f._metadata?.section || f._metadata.section === "general"
          );
          onFiltersChange([...generalFilters, ...newAdvancedFilters]);
        }}
        translations={translations}
      />
      {listSettings && (
        <ColumnSidebarPanel
          open={isColumnSidebarOpen}
          onClose={onColumnSidebarClose}
          fields={listSettings.availableFields}
          visibleFieldOrder={listSettings.visibleFieldOrder}
          onChangeVisibleFieldOrder={listSettings.setVisibleFieldOrder}
          onToggleField={listSettings.toggleField}
          isLoading={listSettings.isLoading}
          systemColumns={systemColumnToggles}
          onSystemColumnToggle={onSystemColumnToggle}
        />
      )}
    </>
  );
}
